import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { TempAgreement } from "@/api/temp-agreements/temp-agreements";
import { Renter } from "@/api/renters/renters";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { api } from "@/api";

interface CompanyFormProps {
  defaults: Partial<TempAgreement>;
  onSubmit: (data: Partial<TempAgreement>) => void;
  onRenterSelect?: (renterId: number) => void;
}

export function CompanyForm({
  defaults,
  onSubmit,
  onRenterSelect,
}: CompanyFormProps) {
  const [existingRenters, setExistingRenters] = useState<Renter[]>([]);
  const [showRenterModal, setShowRenterModal] = useState(false);

  const form = useForm<Partial<TempAgreement>>({
    defaultValues: defaults,
  });

  const handleSubmit = (e: Partial<TempAgreement>) => {
    onSubmit(e);
  };

  useEffect(() => {
    api.renters
      .list()
      .then((response) => {
        if (response.data && response.data.length > 0) {
          setExistingRenters(response.data);
          setShowRenterModal(true);
        }
      })
      .catch(() => {});
  }, []);

  const handleUseExistingRenter = (renter: Renter) => {
    if (onRenterSelect) {
      onRenterSelect(renter.id);

      form.setValue("renter_name", renter.display_name);
      form.setValue("address", renter.address);
      form.setValue("phone_number", renter.phone_number);
      form.setValue("email", renter.email);
      form.setValue("inn", renter.inn);
      form.setValue("ogrn", Number(renter.orgn));
      form.setValue("renter_id", renter.id);
    }
    setShowRenterModal(false);

    setTimeout(() => {
      handleSubmit(form.getValues());
    }, 100);
  };

  const handleCreateNewRenter = () => {
    setShowRenterModal(false);
  };

  return (
    <>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="w-full max-w-2xl space-y-4 p-6 bg-background rounded-xl border-2 border-foreground-200 dark:border-foreground-100"
      >
        <Controller
          control={form.control}
          name="renter_name"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Название компании"
              variant="bordered"
              isRequired
              isInvalid={!!form.formState.errors.renter_name}
              errorMessage={
                form.formState.errors.renter_name?.message as string
              }
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <Controller
          control={form.control}
          name="address"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Адрес"
              variant="bordered"
              isRequired
              isInvalid={!!form.formState.errors.address}
              errorMessage={form.formState.errors.address?.message as string}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={form.control}
            name="phone_number"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Телефон"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.phone_number}
                errorMessage={
                  form.formState.errors.phone_number?.message as string
                }
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={form.control}
            name="email"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Email"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.email}
                errorMessage={form.formState.errors.email?.message as string}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={form.control}
            name="inn"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="ИНН"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.inn}
                errorMessage={form.formState.errors.inn?.message as string}
                value={field.value?.toString() || ""}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={form.control}
            name="ogrn"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="ОГРН"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.ogrn}
                errorMessage={form.formState.errors.ogrn?.message as string}
                value={field.value?.toString() || ""}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <Button type="submit" variant="bordered" color="success">
          Подтвердить
        </Button>
      </form>

      <Modal
        isOpen={showRenterModal}
        onOpenChange={setShowRenterModal}
        scrollBehavior="outside"
      >
        <ModalContent>
          <ModalHeader>Выберите вариант</ModalHeader>
          <ModalBody>
            <p>
              В системе уже зарегистрированы компании. Хотите использовать
              существующую или создать новую?
            </p>

            {existingRenters.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="font-semibold">Существующие компании:</p>
                {existingRenters.map((renter) => (
                  <div
                    key={renter.id}
                    className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                    onClick={() => handleUseExistingRenter(renter)}
                  >
                    <p>
                      <strong>Название:</strong> {renter.display_name}
                    </p>
                    <p>
                      <strong>ИНН:</strong> {renter.inn}
                    </p>
                    <p>
                      <strong>Адрес:</strong> {renter.address}
                    </p>
                    <p>
                      <strong>Телефон:</strong> {renter.phone_number}
                    </p>
                    <p>
                      <strong>Email:</strong> {renter.email}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="light"
              onPress={handleCreateNewRenter}
            >
              Создать новую
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
